/**
 * Scoring and cadence.
 *
 * Pure functions over enums and timestamps — no content, no I/O. The server
 * computes these authoritatively (the client may not write scores); the client
 * runs the same code only to preview what a drill result would do.
 */

import type {
  BackupCondition,
  DrillOutcome,
  HintDepth,
  PhysicalVerificationType,
  ReadinessBand,
} from './enums.js';

export const DAY_MS = 86_400_000;

export const MIN_CADENCE_DAYS = 7;
export const MAX_CADENCE_DAYS = 180;
/** Track B is fixed: a physical check is stale after this many days. */
export const PHYSICAL_INTERVAL_DAYS = 180;
/** Readiness is capped at this while the physical check is overdue. */
export const STALE_PHYSICAL_CEILING = 60;

export const WEIGHTS = {
  recall: 0.5,
  verification: 0.25,
  recency: 0.15,
  condition: 0.1,
} as const;

export const BAND_THRESHOLDS = {
  ready: 85,
  due_soon: 65,
  at_risk: 40,
} as const;

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** 50% component: how deep the user had to dig for a hint. */
export function recallComponent(outcome: DrillOutcome, hintDepthUsed: HintDepth): number {
  if (outcome !== 'success') return 0;
  switch (hintDepthUsed) {
    case 0:
      return 100;
    case 1:
      return 85;
    case 2:
      return 65;
    case 3:
      return 45;
    case 4:
    case 5:
      return 20;
    default:
      return 0;
  }
}

/** 25% component: how hard the evidence was that the backup physically exists. */
export function verificationComponent(type: PhysicalVerificationType): number {
  switch (type) {
    case 'code':
      return 100;
    case 'self':
      return 70;
    case 'memory':
      return 35;
    case 'none':
      return 0;
    default:
      return 0;
  }
}

/**
 * 15% component: full marks inside the cadence, decaying linearly to zero at
 * twice the cadence. A drill that is merely *due* should not tank the score;
 * one that is a whole cycle late should.
 */
export function recencyComponent(daysSinceLastDrill: number, cadenceDays: number): number {
  const cadence = clamp(cadenceDays, MIN_CADENCE_DAYS, MAX_CADENCE_DAYS);
  if (daysSinceLastDrill <= cadence) return 100;
  const overdue = daysSinceLastDrill - cadence;
  return clamp(100 * (1 - overdue / cadence), 0, 100);
}

/** 10% component: reported state of the medium. */
export function conditionComponent(condition: BackupCondition): number {
  switch (condition) {
    case 'intact':
      return 100;
    case 'minor_wear':
      return 70;
    case 'degraded':
      return 40;
    case 'compromised':
      return 0;
    default:
      return 0;
  }
}

export interface RecallScoreInput {
  outcome: DrillOutcome;
  hintDepthUsed: HintDepth;
  physicalVerificationType: PhysicalVerificationType;
  backupCondition: BackupCondition;
  daysSinceLastDrill: number;
  cadenceDays: number;
}

/** The weighted recall score, 0–100, rounded to an integer. */
export function computeRecallScore(input: RecallScoreInput): number {
  const score =
    WEIGHTS.recall * recallComponent(input.outcome, input.hintDepthUsed) +
    WEIGHTS.verification * verificationComponent(input.physicalVerificationType) +
    WEIGHTS.recency * recencyComponent(input.daysSinceLastDrill, input.cadenceDays) +
    WEIGHTS.condition * conditionComponent(input.backupCondition);
  return Math.round(clamp(score, 0, 100));
}

/**
 * Readiness is recall, held down by an overdue physical check.
 *
 * Track A and Track B are independent on purpose: a user who rehearses weekly
 * but has not laid hands on the steel plate in a year is *not* ready, and the
 * ceiling is what says so.
 */
export function computeReadinessScore(recallScore: number, physicalAgeDays: number): number {
  if (physicalAgeDays > PHYSICAL_INTERVAL_DAYS) {
    return Math.min(recallScore, STALE_PHYSICAL_CEILING);
  }
  return recallScore;
}

export function readinessBand(readinessScore: number): ReadinessBand {
  if (readinessScore >= BAND_THRESHOLDS.ready) return 'ready';
  if (readinessScore >= BAND_THRESHOLDS.due_soon) return 'due_soon';
  if (readinessScore >= BAND_THRESHOLDS.at_risk) return 'at_risk';
  return 'action_required';
}

/**
 * Track A cadence after a drill.
 *
 * Unaided success stretches the interval; needing a hint pulls it back to a
 * month; needing a deep hint pulls it to a fortnight; failing pulls it to a
 * week. Spacing expands only when recall actually held.
 */
export function nextCadenceDays(
  currentCadenceDays: number,
  outcome: DrillOutcome,
  hintDepthUsed: HintDepth,
): number {
  const current = clamp(currentCadenceDays, MIN_CADENCE_DAYS, MAX_CADENCE_DAYS);

  if (outcome !== 'success') return MIN_CADENCE_DAYS;
  if (hintDepthUsed === 0) {
    return clamp(Math.round(current * 1.25), MIN_CADENCE_DAYS, MAX_CADENCE_DAYS);
  }
  if (hintDepthUsed >= 4) return 14;
  return clamp(Math.min(current, 30), MIN_CADENCE_DAYS, 30);
}

/** Track B is not adaptive: 180 days from the last time it was physically seen. */
export function nextPhysicalDueAt(lastPhysicalVerifiedAtMs: number): number {
  return lastPhysicalVerifiedAtMs + PHYSICAL_INTERVAL_DAYS * DAY_MS;
}

export function daysBetween(fromMs: number, toMs: number): number {
  return Math.max(0, (toMs - fromMs) / DAY_MS);
}

/**
 * Only `code` verification counts as physically laying hands on the backup.
 * `self` does too — the user says they held it. `memory` and `none` do not.
 */
export function countsAsPhysicalVerification(type: PhysicalVerificationType): boolean {
  return type === 'code' || type === 'self';
}
